import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import Swal from "sweetalert2";
import {
  PropertySavedFilterApplyEvent,
  PropertySavedFilterModel,
  PropertySavedFilterPayload,
  PropertySavedFilterResource,
} from "../../models/property-saved-filter.model";
import { PropertySavedFiltersService } from "../../services/property-saved-filters.service";

@Component({
  selector: "app-saved-property-filters",
  templateUrl: "./saved-property-filters.component.html",
  styleUrls: ["./saved-property-filters.component.scss"],
})
export class SavedPropertyFiltersComponent implements OnInit, OnChanges {
  @Input() resource: PropertySavedFilterResource = "PROPERTY";
  @Input() currentFilters: Record<string, any> | null = null;
  @Input() currentFormValue: Record<string, any> | null = null;
  @Input() selectedStatusId: number | null = null;
  @Input() selectedScope: string | null = null;
  @Input() disabled = false;
  @Input() autoApplyDefault = true;
  @Input() appliedFilterId: string | null = null;

  @Output() apply = new EventEmitter<PropertySavedFilterApplyEvent>();

  savedFilters: PropertySavedFilterModel[] = [];
  selectedFilterId: string | null = null;
  isLoading = false;
  isSaving = false;
  private defaultApplied = false;

  constructor(
    private savedFiltersService: PropertySavedFiltersService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadSavedFilters();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["appliedFilterId"]) {
      this.selectedFilterId = this.appliedFilterId || null;
    }
  }

  get selectedFilter() {
    return (
      this.savedFilters.find((filter) => filter.id === this.selectedFilterId) ||
      null
    );
  }

  get defaultFilter() {
    return this.savedFilters.find((filter) => filter.isDefault) || null;
  }

  loadSavedFilters() {
    this.isLoading = true;
    this.savedFiltersService.getAll(this.resource).subscribe({
      next: (filters) => {
        this.savedFilters = filters || [];
        this.selectedFilterId = this.selectedFilterId || null;
        this.isLoading = false;
        this.applyDefaultFilterOnce();
      },
      error: () => {
        this.savedFilters = [];
        this.isLoading = false;
      },
    });
  }

  onFilterSelected(filterValue: PropertySavedFilterModel | string | null) {
    const filter =
      typeof filterValue === "string"
        ? this.savedFilters.find((savedFilter) => savedFilter.id === filterValue)
        : filterValue;

    if (!filter) return;

    this.applySavedFilter(filter);
  }

  applySavedFilter(filter: PropertySavedFilterModel) {
    this.selectedFilterId = filter.id;
    this.emitApply(filter);
  }

  async saveCurrentFilter() {
    if (this.disabled || this.isSaving) return;

    const result = await this.openSaveFilterDialog();

    if (!result) return;

    this.isSaving = true;
    this.savedFiltersService
      .create({
        resource: this.resource,
        name: result.name,
        filters: this.buildCurrentPayload(),
        isDefault: result.isDefault,
      })
      .subscribe({
        next: (filter) => {
          this.isSaving = false;
          this.savedFilters = [filter, ...this.savedFilters].sort(
            this.sortFilters,
          );
          this.selectedFilterId = filter.id;
          this.successMsg("t-saved-filters.t-saved");
        },
        error: (err) => {
          this.isSaving = false;
          this.errorMsg(this.saveErrorKey(err));
        },
      });
  }

  // O back-end normaliza o nome com trim + colapso de espacos e o unique
  // (user/entidade/recurso/nome) e sensivel a maiusculas. A verificacao no
  // front usa a mesma regra para avisar sem ir ao servidor; o 409 continua a
  // existir como rede de seguranca (outro separador, outra sessao).
  private normalizeFilterName(name: string): string {
    return `${name ?? ""}`.trim().replace(/\s+/g, " ");
  }

  private isDuplicatedName(name: string, ignoreId?: string): boolean {
    const normalized = this.normalizeFilterName(name);
    return this.savedFilters.some(
      (filter) =>
        filter.id !== ignoreId &&
        this.normalizeFilterName(filter.name) === normalized,
    );
  }

  // O back-end devolve 409 quando ja existe uma pesquisa com o mesmo nome
  // (unique user/entidade/recurso/nome).
  private saveErrorKey(err: any): string {
    return err?.status === 409
      ? "t-saved-filters.t-name-duplicate"
      : "t-saved-filters.t-save-error";
  }

  private async openSaveFilterDialog(): Promise<{
    name: string;
    isDefault: boolean;
  } | null> {
    const resourceName = this.translateService.instant(
      this.resource === "PROPERTY"
        ? "t-saved-filters.t-resource-properties"
        : "t-saved-filters.t-resource-developments",
    );
    const placeholder = this.translateService.instant(
      "t-saved-filters.t-name-placeholder",
    );
    const defaultTitle = this.translateService.instant(
      "t-saved-filters.t-default-question",
    );
    const defaultText = this.translateService.instant(
      "t-saved-filters.t-default-help",
      { resource: resourceName },
    );

    const result = await Swal.fire({
      title: this.translateService.instant("t-saved-filters.t-save-title"),
      html: `
        <div class="text-start">
          <label class="form-label" for="saved-filter-name">
            ${this.escapeHtml(this.translateService.instant("t-name"))}
          </label>
          <input id="saved-filter-name" class="swal2-input m-0 w-100" type="text"
            maxlength="60" placeholder="${this.escapeHtml(placeholder)}">
          <div class="form-check mt-3">
            <input class="form-check-input" type="checkbox" id="saved-filter-default">
            <label class="form-check-label fw-semibold" for="saved-filter-default">
              ${this.escapeHtml(defaultTitle)}
            </label>
            <div class="form-text">${this.escapeHtml(defaultText)}</div>
          </div>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonColor: "rgb(54, 69, 116)",
      confirmButtonText: this.translateService.instant("t-save"),
      cancelButtonText: this.translateService.instant("t-cancel"),
      didOpen: () => {
        Swal.getPopup()
          ?.querySelector<HTMLInputElement>("#saved-filter-name")
          ?.focus();
      },
      preConfirm: () => {
        const name = Swal.getPopup()
          ?.querySelector<HTMLInputElement>("#saved-filter-name")
          ?.value?.trim();
        const isDefault =
          Swal.getPopup()?.querySelector<HTMLInputElement>(
            "#saved-filter-default",
          )?.checked === true;

        if (!name) {
          Swal.showValidationMessage(
            this.translateService.instant("t-saved-filters.t-name-required"),
          );
          return false;
        }

        if (this.isDuplicatedName(name)) {
          Swal.showValidationMessage(
            this.translateService.instant("t-saved-filters.t-name-duplicate"),
          );
          return false;
        }

        return { name, isDefault };
      },
    });

    if (!result.isConfirmed || !result.value) return null;

    return result.value as { name: string; isDefault: boolean };
  }

  async renameSelectedFilter() {
    const filter = this.selectedFilter;
    if (!filter) return;

    this.renameFilter(filter);
  }

  async renameFilter(filter: PropertySavedFilterModel, event?: Event) {
    event?.stopPropagation();

    const result = await Swal.fire({
      title: this.translateService.instant("t-saved-filters.t-rename-title"),
      input: "text",
      inputValue: filter.name,
      showCancelButton: true,
      confirmButtonColor: "rgb(54, 69, 116)",
      confirmButtonText: this.translateService.instant("t-save"),
      cancelButtonText: this.translateService.instant("t-cancel"),
      inputValidator: (value) => {
        if (!value?.trim()) {
          return this.translateService.instant(
            "t-saved-filters.t-name-required",
          );
        }
        if (this.isDuplicatedName(value, filter.id)) {
          return this.translateService.instant(
            "t-saved-filters.t-name-duplicate",
          );
        }
        return null;
      },
    });

    if (!result.isConfirmed || !result.value) return;

    this.savedFiltersService
      .update(filter.id, { name: result.value })
      .subscribe({
        next: (updatedFilter) => {
          this.replaceFilter(updatedFilter);
          this.successMsg("t-saved-filters.t-updated");
        },
        error: (err) => this.errorMsg(this.saveErrorKey(err)),
      });
  }

  updateSelectedFilter() {
    const filter = this.selectedFilter;
    if (!filter) return;

    this.updateFilter(filter);
  }

  updateFilter(filter: PropertySavedFilterModel, event?: Event) {
    event?.stopPropagation();

    this.savedFiltersService
      .update(filter.id, { filters: this.buildCurrentPayload() })
      .subscribe({
        next: (updatedFilter) => {
          this.replaceFilter(updatedFilter);
          this.successMsg("t-saved-filters.t-updated");
        },
        error: (err) => this.errorMsg(this.saveErrorKey(err)),
      });
  }

  setSelectedAsDefault() {
    const filter = this.selectedFilter;
    if (!filter) return;

    this.setAsDefault(filter);
  }

  setAsDefault(filter: PropertySavedFilterModel, event?: Event) {
    event?.stopPropagation();

    this.savedFiltersService.setDefault(filter.id).subscribe({
      next: (updatedFilter) => {
        this.savedFilters = this.savedFilters
          .map((savedFilter) => ({
            ...savedFilter,
            isDefault: savedFilter.id === updatedFilter.id,
          }))
          .sort(this.sortFilters);
        this.selectedFilterId = updatedFilter.id;
        this.successMsg("t-saved-filters.t-default-updated");
      },
      error: () => this.errorMsg("t-saved-filters.t-save-error"),
    });
  }

  async deleteSelectedFilter() {
    const filter = this.selectedFilter;
    if (!filter) return;

    this.deleteFilter(filter);
  }

  async deleteFilter(filter: PropertySavedFilterModel, event?: Event) {
    event?.stopPropagation();

    const result = await Swal.fire({
      title: this.translateService.instant("t-saved-filters.t-delete-title"),
      text: this.translateService.instant("t-saved-filters.t-delete-text", {
        name: filter.name,
      }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "rgb(240, 101, 72)",
      confirmButtonText: this.translateService.instant("t-delete"),
      cancelButtonText: this.translateService.instant("t-cancel"),
    });

    if (!result.isConfirmed) return;

    this.savedFiltersService.delete(filter.id).subscribe({
      next: () => {
        this.savedFilters = this.savedFilters.filter(
          (savedFilter) => savedFilter.id !== filter.id,
        );
        this.selectedFilterId = null;
        this.successMsg("t-saved-filters.t-deleted");
      },
      error: () => this.errorMsg("t-saved-filters.t-delete-error"),
    });
  }

  private applyDefaultFilterOnce() {
    if (!this.autoApplyDefault || this.defaultApplied) return;

    const defaultFilter = this.savedFilters.find((filter) => filter.isDefault);
    if (!defaultFilter) return;

    this.defaultApplied = true;
    this.selectedFilterId = defaultFilter.id;
    setTimeout(() => this.emitApply(defaultFilter));
  }

  private emitApply(filter: PropertySavedFilterModel) {
    this.apply.emit({
      filter,
      payload: filter.filters,
    });
  }

  private buildCurrentPayload(): PropertySavedFilterPayload {
    return {
      version: 1,
      filters: this.cleanFilters(this.currentFilters || {}),
      formValue: this.cleanFormValue(this.currentFormValue || {}),
      ui: {
        selectedStatusId: this.selectedStatusId,
        selectedScope: this.selectedScope,
      },
    };
  }

  private cleanFilters(filters: Record<string, any>) {
    const data = this.clone(filters);
    ["page", "exportId", "isExported", "developments"].forEach(
      (key) => delete data[key],
    );
    return data;
  }

  private cleanFormValue(formValue: Record<string, any>) {
    return this.clone(formValue);
  }

  private replaceFilter(updatedFilter: PropertySavedFilterModel) {
    this.savedFilters = this.savedFilters
      .map((filter) => (filter.id === updatedFilter.id ? updatedFilter : filter))
      .sort(this.sortFilters);
  }

  private sortFilters(
    a: PropertySavedFilterModel,
    b: PropertySavedFilterModel,
  ) {
    if (a.isDefault !== b.isDefault) {
      return a.isDefault ? -1 : 1;
    }

    return (a.order || 0) - (b.order || 0);
  }

  private clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value || {}));
  }

  private escapeHtml(value: string) {
    return `${value || ""}`
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  private successMsg(key: string) {
    Swal.fire({
      icon: "success",
      title: this.translateService.instant(key),
      timer: 1400,
      showConfirmButton: false,
    });
  }

  private errorMsg(key: string) {
    Swal.fire({
      icon: "error",
      title: this.translateService.instant(key),
      confirmButtonColor: "rgb(54, 69, 116)",
      confirmButtonText: this.translateService.instant("t-close"),
    });
  }
}
